import type {
    CatalogImportNameDecision,
    CatalogImportNameTarget,
} from '@/api/handlers/resources/catalogImports';
import type {
    CatalogImportNameAction,
    CatalogImportNameResult,
    CatalogImportNamesResult,
    CatalogImportNameSuggestion,
} from '@/api/models';
import {
    nameTextKey,
    type NameSuggestions,
    type NamesDecisions,
    type ParsedLine,
} from '@/pages/catalogImport/store';

/**
 * The Names step's rules, pure: which of the preview's names the owner
 * reviews, the draft each one starts from (the owner's decision, else the
 * step's default for that preview), what the preview answered for it, and
 * the request `names` the drafts become. The step's hook owns the drafts;
 * this file owns what they mean.
 */

/** The three kinds of name, keyed as the preview and the request key them. */
export type NameKind = keyof CatalogImportNamesResult;

export const NAME_KINDS: readonly NameKind[] = [
    'brands',
    'categories',
    'suppliers',
];

/** The actions each kind offers, in the segmented control's order. Nest is for categories only. */
export const NAME_ACTIONS: Record<
    NameKind,
    readonly CatalogImportNameAction[]
> = {
    brands: ['create', 'useExisting', 'rename'],
    categories: ['create', 'useExisting', 'rename', 'nest'],
    suppliers: ['create', 'useExisting', 'rename'],
};

/**
 * What the owner has chosen for one name. Every action's value is kept, so
 * switching from Rename to Create and back finds the typed name again; only
 * the chosen action's value is sent.
 */
export type NameDraft = {
    action: CatalogImportNameAction;
    /** Use existing: an entity of the catalogue, or another new text of the file. */
    target: CatalogImportNameTarget | null;
    /** Rename: the name it is created under. */
    name: string;
    /** Nest under: the existing category it goes inside. */
    parentId: string | null;
    /**
     * Categories: add the axes the rows use that the category lacks
     * (`axesToAdd`). Sent only as `false`, and only while the row shows them.
     */
    addAxes: boolean;
};

const EMPTY_DRAFT: NameDraft = {
    action: 'create',
    target: null,
    name: '',
    parentId: null,
    addAxes: true,
};

/** Each kind's drafts, by the text as the preview lists it. */
export type NameDrafts = Record<NameKind, ReadonlyMap<string, NameDraft>>;

/**
 * How a text stands against the catalogue, which decides what the owner can
 * do with it:
 * - `new`: no entity has that name; Create, Use existing, Rename (and Nest
 *   under) all apply.
 * - `existing`: exactly one entity has it; the server uses that entity
 *   whatever the decision says, so only the axes checkbox is left to decide.
 * - `ambiguous`: several entities have it; only picking one of the
 *   candidates settles it.
 */
export type NameMatch = 'new' | 'existing' | 'ambiguous';

/** A name the owner reviews: the preview's entry, how it matched, and what was answered. */
export type ReviewedName = CatalogImportNameResult & {
    match: NameMatch;
    /**
     * What the preview resolved the name as: the decision it was sent, else
     * the server's own action. Its problems and `axesToAdd` belong to this
     * draft, not to an edit made since.
     */
    answered: NameDraft;
};

/** The names the owner reviews, and the drafts they start from. */
export type NamesReview = {
    entries: Record<NameKind, ReviewedName[]>;
    /** The draft each entry starts from: the owner's decision, else the step's default. */
    drafts: NameDrafts;
};

/** The codes of a name several entities share; a candidate pick settles them. */
const AMBIGUOUS_CODES: ReadonlySet<string> = new Set([
    'BRAND_AMBIGUOUS',
    'CATEGORY_AMBIGUOUS',
    'SUPPLIER_AMBIGUOUS',
]);

/** The server matches texts trimmed and case-insensitively; so does this file. */
const sameText = (a: string, b: string): boolean =>
    a.trim().toLowerCase() === b.trim().toLowerCase();

/** The decision the store holds for `text`, if any. */
export const decisionFor = (
    decisions: readonly CatalogImportNameDecision[],
    text: string,
): CatalogImportNameDecision | undefined =>
    decisions.find((decision) => sameText(decision.text, text));

/**
 * How the preview's entry stands against the catalogue. The server answers
 * Use existing on its own only for a text that names an entity: with
 * candidates when several share the name, else with the one it resolved.
 * The step never sends Use existing for such a text (only its axes choice),
 * so a Use existing the owner decided always marks a `new` text.
 */
export const matchOf = (
    entry: CatalogImportNameResult,
    decision: CatalogImportNameDecision | undefined,
): NameMatch => {
    if (entry.action !== 'useExisting') {
        return 'new';
    }
    if (entry.candidates.length > 0) {
        return 'ambiguous';
    }
    return entry.resolvedId !== null &&
        entry.problems.length === 0 &&
        decision?.action !== 'useExisting'
        ? 'existing'
        : 'new';
};

/**
 * Whether the owner reviews this name. A text that names exactly one entity
 * is not new: the server uses that entity whatever the decision says, so it
 * is left out, unless it is a category lacking axes its rows use (the owner
 * decides whether to add them). Every other text is listed: an unknown one,
 * a decided one, and an ambiguous one (several entities share the name).
 */
export const isReviewed = (
    entry: CatalogImportNameResult,
    match: NameMatch,
): boolean => match !== 'existing' || entry.axesToAdd.length > 0;

/**
 * A suggestion as a Use existing target: the entity, or the other text of
 * the file. A `rename` suggestion names no target, so it has none.
 */
export const targetOfSuggestion = (
    suggestion: CatalogImportNameSuggestion,
): CatalogImportNameTarget | null => {
    if ('id' in suggestion) {
        return { id: suggestion.id };
    }
    return 'text' in suggestion ? { text: suggestion.text } : null;
};

/**
 * What the typo line names: the entity's name, or the other text. A
 * `rename` suggestion has no typo line.
 */
export const suggestionLabelOf = (
    suggestion: CatalogImportNameSuggestion,
): string | null => {
    if ('id' in suggestion) {
        return suggestion.name;
    }
    return 'text' in suggestion ? suggestion.text : null;
};

/**
 * The typo line's name ("looks like a typo · Samsung"): the correction a
 * `likelyTypo` suggestion of this preview names. It goes once the
 * accepted suggestion is checked (the server suggests only for a text sent
 * as Create); the decision stays. A joined text's suggestion has no typo
 * line: it says why instead (`joinedPartsOf`).
 */
export const typoLabelOf = (entry: CatalogImportNameResult): string | null =>
    entry.suggestion?.reason === 'likelyTypo'
        ? suggestionLabelOf(entry.suggestion)
        : null;

/** A joined text's parts: split at `,` and `;`, blank parts left out. */
const JOINED_SEPARATOR = /[,;]/;

/**
 * How many names a joined text lists, when its suggestion is the first of
 * them (`joinedList`); `null` otherwise. The row says why with it ("This
 * cell lists 2 suppliers…"). It reads this preview's suggestion, else the
 * one the store remembers for the text: the server suggests only for a
 * text sent as Create, and the cell lists several names whatever the
 * owner chose, so the reason survives a re-check.
 */
export const joinedPartsOf = (
    entry: CatalogImportNameResult,
    remembered: CatalogImportNameSuggestion | undefined,
): number | null =>
    (entry.suggestion ?? remembered)?.reason === 'joinedList'
        ? entry.text
              .split(JOINED_SEPARATOR)
              .filter((part) => part.trim() !== '').length
        : null;

/**
 * A category the catalogue already has, listed only because the import
 * adds axes its rows use (`axesToAdd`): it is not new, so the step lists
 * it apart from the new categories ("Existing categories that gain
 * options").
 */
export const isGainingOptions = (name: ReviewedName): boolean =>
    name.match === 'existing' && name.axesToAdd.length > 0;

/**
 * The decision a suggestion proposes: Use existing with its target (an
 * entity, or another text of the file), or Rename with its name. Every
 * shape is preselected the same way, whatever its `reason`.
 */
const suggestedDecisionOf = (
    text: string,
    suggestion: CatalogImportNameSuggestion | null | undefined,
): CatalogImportNameDecision | null => {
    if (!suggestion) {
        return null;
    }
    if ('rename' in suggestion) {
        return { text, action: 'rename', name: suggestion.rename };
    }
    const target = targetOfSuggestion(suggestion);
    return target ? { text, action: 'useExisting', target } : null;
};

/**
 * The decision the step proposes for a name the owner has not decided on,
 * from the preview alone: the suggestion (Use existing with its target, or
 * Rename with its name) for a likely typo or a joined text; Use existing
 * with nothing picked for an ambiguous name; only the text (the server's
 * own match) for an existing one; else Create.
 */
const defaultDecisionOf = (
    entry: CatalogImportNameResult,
): CatalogImportNameDecision => {
    const { text } = entry;
    switch (matchOf(entry, undefined)) {
        case 'existing':
            return { text };
        case 'ambiguous':
            return { text, action: 'useExisting', target: null };
        case 'new':
            return (
                suggestedDecisionOf(text, entry.suggestion) ?? {
                    text,
                    action: 'create',
                }
            );
    }
};

/**
 * The decisions the step proposes for one preview, before the owner's. The
 * step derives them once per preview and puts the owner's decisions over
 * them (`reviewOf`), so a re-check never undoes a manual choice: a typo the
 * owner switched back to Create stays Create, though the next preview
 * suggests it again.
 */
export const deriveDefaultDecisions = (
    names: CatalogImportNamesResult,
): NamesDecisions => ({
    brands: names.brands.map(defaultDecisionOf),
    categories: names.categories.map(defaultDecisionOf),
    suppliers: names.suppliers.map(defaultDecisionOf),
});

/**
 * The draft a decision stands for. An existing name is always its entity;
 * an ambiguous one is always Use existing, with the entity picked, if any.
 */
const draftOf = (
    entry: CatalogImportNameResult,
    match: NameMatch,
    decision: CatalogImportNameDecision,
): NameDraft => {
    const addAxes = decision.addAxes ?? true;
    switch (match) {
        case 'existing':
            return {
                ...EMPTY_DRAFT,
                action: 'useExisting',
                target:
                    entry.resolvedId === null ? null : { id: entry.resolvedId },
                addAxes,
            };
        case 'ambiguous':
            return {
                ...EMPTY_DRAFT,
                action: 'useExisting',
                target:
                    decision.action === 'useExisting' &&
                    decision.target &&
                    'id' in decision.target
                        ? decision.target
                        : null,
                addAxes,
            };
        case 'new':
            return {
                action: decision.action ?? 'create',
                target: decision.target ?? null,
                name: decision.name ?? '',
                parentId: decision.parentId ?? null,
                addAxes,
            };
    }
};

/** What the preview was answering: the decision it was sent, else the server's own action. */
const answeredDecisionOf = (
    entry: CatalogImportNameResult,
    decision: CatalogImportNameDecision | undefined,
): CatalogImportNameDecision => {
    if (decision) {
        return decision;
    }
    return entry.action === 'useExisting'
        ? {
              text: entry.text,
              action: 'useExisting',
              target:
                  entry.resolvedId === null ? null : { id: entry.resolvedId },
          }
        : { text: entry.text, action: 'create' };
};

/**
 * The names to review in a preview, each with how it matched and what the
 * preview answered, and the draft it starts from: the owner's decision,
 * else the step's default (`deriveDefaultDecisions`).
 *
 * `checked` are the decisions the preview was sent; how a text matched and
 * what was answered come from them. The owner's `decisions` can be ahead of
 * them (Back from Names saves choices that were not checked) and only set
 * where each draft starts.
 */
export const reviewOf = (
    names: CatalogImportNamesResult,
    decisions: NamesDecisions,
    checked: NamesDecisions = decisions,
): NamesReview => {
    const defaults = deriveDefaultDecisions(names);
    const entries = {} as NamesReview['entries'];
    const drafts = {} as Record<NameKind, Map<string, NameDraft>>;
    for (const kind of NAME_KINDS) {
        entries[kind] = [];
        drafts[kind] = new Map();
        for (const entry of names[kind]) {
            const sent = decisionFor(checked[kind], entry.text);
            const match = matchOf(entry, sent);
            if (!isReviewed(entry, match)) {
                continue;
            }
            const starting = decisionFor(decisions[kind], entry.text) ??
                decisionFor(defaults[kind], entry.text) ?? {
                    text: entry.text,
                };
            entries[kind].push({
                ...entry,
                match,
                answered: draftOf(
                    entry,
                    match,
                    answeredDecisionOf(entry, sent),
                ),
            });
            drafts[kind].set(entry.text, draftOf(entry, match, starting));
        }
    }
    return { entries, drafts };
};

/**
 * The drafts for a new preview: an entry the owner already has a draft for
 * keeps it; a new entry starts from the preview. Entries the preview no
 * longer lists are dropped.
 */
export const mergeDrafts = (
    current: NameDrafts,
    review: NamesReview,
): NameDrafts => {
    const merged = {} as Record<NameKind, Map<string, NameDraft>>;
    for (const kind of NAME_KINDS) {
        merged[kind] = new Map(
            review.entries[kind].map((entry) => [
                entry.text,
                current[kind].get(entry.text) ??
                    review.drafts[kind].get(entry.text) ??
                    EMPTY_DRAFT,
            ]),
        );
    }
    return merged;
};

/** Whether the draft can be sent: every action but Create needs its value. */
export const isDraftComplete = (draft: NameDraft): boolean => {
    switch (draft.action) {
        case 'create':
            return true;
        case 'useExisting':
            return draft.target !== null;
        case 'rename':
            return draft.name.trim() !== '';
        case 'nest':
            return draft.parentId !== null;
    }
};

/** Two drafts that send the same decision. */
export const isSameDraft = (a: NameDraft, b: NameDraft): boolean => {
    if (a.action !== b.action) {
        return false;
    }
    switch (a.action) {
        case 'create':
            return true;
        case 'useExisting':
            return targetKeyOf(a.target) === targetKeyOf(b.target);
        case 'rename':
            return a.name.trim() === b.name.trim();
        case 'nest':
            return a.parentId === b.parentId;
    }
};

/** A decision as a draft, its blanks read as the request reads them. */
const draftOfDecision = (decision: CatalogImportNameDecision): NameDraft => ({
    action: decision.action ?? 'create',
    target: decision.target ?? null,
    name: decision.name ?? '',
    parentId: decision.parentId ?? null,
    addAxes: decision.addAxes ?? true,
});

/**
 * Two decisions that ask for the same: the same action with the same value
 * for it (`isSameDraft`), and the same axes choice. A blank action is
 * Create and a blank `addAxes` is `true`, as the request reads them.
 */
export const isSameDecision = (
    a: CatalogImportNameDecision,
    b: CatalogImportNameDecision,
): boolean => {
    const draftA = draftOfDecision(a);
    const draftB = draftOfDecision(b);
    return isSameDraft(draftA, draftB) && draftA.addAxes === draftB.addAxes;
};

/**
 * What the step proposed for a text: its default in this preview, except
 * for a text that was suggested something before and is suggested nothing
 * now. The server suggests only for a text sent as Create, so the answer
 * to a kept preselection carries none; the step still proposed the
 * suggestion it remembers (Use existing with its target, or Rename).
 */
const proposedDecisionOf = (
    entry: CatalogImportNameResult,
    remembered: CatalogImportNameSuggestion | undefined,
): CatalogImportNameDecision =>
    (entry.suggestion === null
        ? suggestedDecisionOf(entry.text, remembered)
        : null) ?? defaultDecisionOf(entry);

/**
 * How many of the store's decisions differ from what the step proposed for
 * their text: the default in this preview (`deriveDefaultDecisions`), or,
 * for a text an earlier answer suggested something for, that suggestion
 * (`suggestions`, the store's: Use existing with its target, or Rename). Another action, another
 * value for it, or the axes unticked count; checked or not makes no
 * difference, so a kept preselection never counts, however often it is
 * checked. A decision for a text the names no longer list does not
 * count, and nothing does without names. This is the Names part of the
 * work a new file would lose (`importWorkOf` in `work/`, which passes the
 * preview's names or, while a new branch has dropped it, the latest
 * answer's).
 */
export const changedDecisionCount = (
    names: CatalogImportNamesResult | undefined,
    decisions: NamesDecisions,
    suggestions?: NameSuggestions,
): number => {
    if (!names) {
        return 0;
    }
    return NAME_KINDS.reduce((count, kind) => {
        const proposed = names[kind].map((entry) =>
            proposedDecisionOf(
                entry,
                suggestions?.[kind].get(nameTextKey(entry.text)),
            ),
        );
        return (
            count +
            decisions[kind].filter((decision) => {
                const byDefault = decisionFor(proposed, decision.text);
                return (
                    byDefault !== undefined &&
                    !isSameDecision(decision, byDefault)
                );
            }).length
        );
    }, 0);
};

/**
 * The preview found several entities named as the draft decides, and the
 * draft is still the decision it answered: sending it again would answer
 * the same. A pick (or, after a Rename, another name) settles it.
 */
const isAmbiguityOpen = (name: ReviewedName, draft: NameDraft): boolean =>
    name.problems.some((problem) => AMBIGUOUS_CODES.has(problem.code)) &&
    isSameDraft(draft, name.answered);

/**
 * Whether the name is ready for Check products: an existing one always is;
 * an ambiguous one once a candidate is picked; a new one once its action
 * has its value and no ambiguity of its own is left open.
 */
export const isNameSettled = (
    name: ReviewedName,
    draft: NameDraft,
): boolean => {
    switch (name.match) {
        case 'existing':
            return true;
        case 'ambiguous':
            return (
                draft.target !== null &&
                'id' in draft.target &&
                !isAmbiguityOpen(name, draft)
            );
        case 'new':
            return isDraftComplete(draft) && !isAmbiguityOpen(name, draft);
    }
};

/**
 * Whether the row shows its `axesToAdd` with the checkbox. The list belongs
 * to the decision the preview answered, so it shows while the draft is
 * that decision, and through a switch between Create, Rename and Nest under
 * (each makes a new category that takes the rows' axes). A switch to Use
 * existing names another category, whose missing axes the next check lists.
 */
export const showsAxes = (name: ReviewedName, draft: NameDraft): boolean => {
    if (name.axesToAdd.length === 0) {
        return false;
    }
    return (
        name.match === 'existing' ||
        isSameDraft(draft, name.answered) ||
        (draft.action !== 'useExisting' &&
            name.answered.action !== 'useExisting' &&
            name.resolvedId === null)
    );
};

/**
 * The axes each category row showed with its checkbox when the decisions
 * were sent, by the text as the server matches it (trimmed, ignoring case).
 * A row that showed none maps to an empty set.
 */
export type ShownAxes = ReadonlyMap<string, ReadonlySet<string>>;

const textKey = (text: string): string => text.trim().toLowerCase();

/** What the category rows show now (`showsAxes`), to send with the decisions. */
export const shownAxesOf = (
    entries: readonly ReviewedName[],
    drafts: ReadonlyMap<string, NameDraft>,
): ShownAxes =>
    new Map(
        entries.map((entry) => [
            textKey(entry.text),
            new Set(
                showsAxes(entry, drafts.get(entry.text) ?? entry.answered)
                    ? entry.axesToAdd.map((axis) => axis.attributeTypeId)
                    : [],
            ),
        ]),
    );

/**
 * Categories a check's answer adds axes to that the owner was not shown
 * when the decisions were sent: a Use existing (a preselected typo, a target
 * changed since) names another category, whose missing axes only this
 * answer lists. The import would add them (`addAxes`) without the owner
 * ever seeing the checkbox, so the step stays for them to be looked at.
 */
export const unreviewedAxesCount = (
    entries: readonly ReviewedName[],
    shown: ShownAxes,
): number =>
    entries.filter(
        (entry) =>
            entry.addAxes &&
            entry.axesToAdd.some(
                (axis) =>
                    !shown.get(textKey(entry.text))?.has(axis.attributeTypeId),
            ),
    ).length;

/**
 * Accept all: Create on every new name of the section, other values kept.
 * An existing or ambiguous name is left as it is: Create does not apply to
 * a name the catalogue already has.
 */
export const acceptAll = (
    entries: readonly ReviewedName[],
    drafts: ReadonlyMap<string, NameDraft>,
): ReadonlyMap<string, NameDraft> => {
    const fixed = new Set(
        entries
            .filter((entry) => entry.match !== 'new')
            .map((entry) => entry.text),
    );
    return new Map(
        [...drafts].map(([text, draft]) => [
            text,
            fixed.has(text) ? draft : { ...draft, action: 'create' },
        ]),
    );
};

/**
 * The request's decision for one draft: the chosen action and its value
 * only, plus `addAxes: false` when the owner unticked the axes the row
 * shows. An existing name sends only its text (and that choice): the server
 * uses its entity whatever the action says.
 */
export const decisionOf = (
    name: ReviewedName,
    draft: NameDraft,
): CatalogImportNameDecision => {
    const { text } = name;
    const axes =
        showsAxes(name, draft) && !draft.addAxes ? { addAxes: false } : {};
    if (name.match === 'existing') {
        return { text, ...axes };
    }
    switch (draft.action) {
        case 'create':
            return { text, action: 'create', ...axes };
        case 'useExisting':
            return {
                text,
                action: 'useExisting',
                target: draft.target,
                ...axes,
            };
        case 'rename':
            return { text, action: 'rename', name: draft.name.trim(), ...axes };
        case 'nest':
            return { text, action: 'nest', parentId: draft.parentId, ...axes };
    }
};

/** The request's `names`: one decision per reviewed name, in the preview's order. */
export const namesDecisionsOf = (
    entries: NamesReview['entries'],
    drafts: NameDrafts,
): NamesDecisions => {
    const decisions = {} as NamesDecisions;
    for (const kind of NAME_KINDS) {
        decisions[kind] = entries[kind].map((entry) =>
            decisionOf(entry, drafts[kind].get(entry.text) ?? EMPTY_DRAFT),
        );
    }
    return decisions;
};

/**
 * How many reviewed names are not ready for Check products: an action
 * without its value, or an ambiguous name without a pick.
 */
export const incompleteCount = (
    entries: NamesReview['entries'],
    drafts: NameDrafts,
): number =>
    NAME_KINDS.reduce(
        (count, kind) =>
            count +
            entries[kind].filter((entry) => {
                const draft = drafts[kind].get(entry.text);
                return draft !== undefined && !isNameSettled(entry, draft);
            }).length,
        0,
    );

/** Rows the import keeps with an empty Category cell: they stay Unsorted. */
export const unsortedRowCount = (
    lines: readonly ParsedLine[],
    exclusions: ReadonlySet<number>,
): number =>
    lines.filter(
        (line) =>
            !exclusions.has(line.rowNumber) &&
            !line.categoryId?.trim() &&
            !line.categoryText?.trim(),
    ).length;

/** Marks a picker key as another new text of the file rather than an entity id. */
const TEXT_KEY_PREFIX = 'text:';

/**
 * A Use existing target as a picker key. Entities and the file's texts share
 * one list: an entity is keyed by its id, as every catalogue picker keys it,
 * and a text of the file by `text:` and the text.
 */
export const targetKeyOf = (
    target: CatalogImportNameTarget | null,
): string | null => {
    if (target === null) {
        return null;
    }
    return 'id' in target ? target.id : `${TEXT_KEY_PREFIX}${target.text}`;
};

/** The target a picker key names; `null` for an empty key. */
export const targetOfKey = (key: string): CatalogImportNameTarget | null => {
    if (key.startsWith(TEXT_KEY_PREFIX)) {
        const text = key.slice(TEXT_KEY_PREFIX.length);
        return text ? { text } : null;
    }
    return key ? { id: key } : null;
};
